import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';

import { idToLink } from 'inside-out/managers/navManager';

import { PageHeader } from 'inside-out/components/shared/pageHeader';
import { PageNav } from 'inside-out/components/shared/pageNav';
import { Link } from 'inside-out/components/shared/link';
import { Cards } from 'inside-out/components/shared/cards';
import { Pagination } from 'inside-out/components/shared/pagination';
import { ColouredCard } from 'inside-out/components/shared/cards/colouredCard';
import { EmploymentCard } from 'inside-out/components/shared/cards/employmentCard';
import { navigateTo } from 'inside-out/components/shared/link';

import {
	innerWrapperStyle,
	employmentInterestedCardStyle,
	researchBannerInnerStyle,
	researchBannerStyle,
	researchBannerButtonSection,
	researchButton,
} from 'inside-out/page-styles/employment';

import {
	grey,
	aqua,
	darkGrey,
	cardSection,
	contentSection,
	button,
	pink,
	mobileMaxWidthBreakpoint,
} from 'inside-out/global-styles';

const linkWithFilter = ({ type, page }) => {
	let pieces = ['employment'];
	if (type === 'in-sector') {
		pieces.push('in-sector');
	}
	if ((page || 1) !== 1) pieces.push(page);
	return `/${pieces.join('/')}`;
};

export default class EmploymentPage extends React.PureComponent {
	static propTypes = {
		pathContext: PropTypes.shape({
			pageNumber: PropTypes.number.isRequired,
			totalPages: PropTypes.number.isRequired,
			type: PropTypes.string,
		}).isRequired,

		data: PropTypes.shape({
			page: PropTypes.shape({
				edges: PropTypes.arrayOf(
					PropTypes.shape({
						node: PropTypes.shape({
							heading: PropTypes.string.isRequired,
							body: PropTypes.string.isRequired,
						}).isRequired,
					})
				).isRequired,
			}).isRequired,

			employment: PropTypes.shape({
				edges: PropTypes.arrayOf(
					PropTypes.shape({
						node: PropTypes.shape({
							id: PropTypes.string.isRequired,
							title: PropTypes.string.isRequired,
							organisation: PropTypes.string.isRequired,
							organisation_logo: PropTypes.string.isRequired,
							extract: PropTypes.string.isRequired,
						}),
					})
				),
			}),
		}),
	};

	render() {
		const { type } = this.props.pathContext;
		const { pageNumber, totalPages } = this.props.pathContext;
		const { employment } = this.props.data;
		const pageSettings = this.props.data.page.edges[0].node;

		const EmploymentInterestedCard = () => {
			return (
				<Link
					to="/employment/expression-of-interest"
					data-component="Card"
					className={employmentInterestedCardStyle}
				>
					<article>
						<h1>Interested in contributing to our work at InsideOut?</h1>
						<p>Complete an expression of interest and we’ll will get back to you.</p>
						<span className={button(pink)}>Apply Now</span>
					</article>
				</Link>
			);
		};

		return (
			<React.Fragment>
				<Helmet title="Employment">
					<meta property="og:type" content="website"/>
			    <meta property="og:title" content="InsideOut - Employment"/>
				</Helmet>

				<PageHeader
					background="#fff"
					titleText={pageSettings.heading}
					titleTextColor={darkGrey}
					subTitleText={pageSettings.body}
					subTitleTextColor={grey}
					pageNav={
						<PageNav
							textColor={grey}
							activeTextColor={darkGrey}
							activeUnderlineColor={aqua}
							links={[
								{
									to: '/employment',
									text: 'Employment at InsideOut',
									isActive: () => type === 'inside-out-position',
								},
								{
									to: '/employment/in-sector',
									text: 'Employment in the sector across Australia',
									isActive: () => type === 'in-sector',
								},
							]}
						/>
					}
				/>

				<section className={contentSection('#F6F6F6', '30px')}>
					<div className={innerWrapperStyle}>
						<div className={cardSection()}>
							<Cards
								columns={2}
								scrollable={false}
								columnsAt={{
									[`max-width: ${mobileMaxWidthBreakpoint}`]: 1,
								}}
							>
								{type === 'in-sector' && pageNumber === 1 && (
									<ColouredCard
										id={0}
										to="/employment/submit"
										minHeight="auto"
										background={pink}
										title="Want to advertise an employment opportunity?"
										body="If you wish to advertise an employment opportunity in the eating disorders sector on our website, let us know."
										arrow={true}
									/>
								)}
								{employment &&
									employment.edges.map(({ node }, index) => (
										<EmploymentCard
											key={index}
											to={idToLink(node.id)}
											title={node.title}
											body={node.extract}
											linkText="View Details"
											img={node.organisation_logo}
										/>
									))}
								{type === 'inside-out-position' && <EmploymentInterestedCard />}
							</Cards>
						</div>
						{ totalPages !== 1 && (
							<Pagination
								initialPage={ pageNumber }
								pageCount={ totalPages }
								onPageChange={ page => {
									navigateTo(linkWithFilter({ type, page }));
								} }
							/>
						) }
					</div>
				</section>

				{pageSettings.blue_banner_text && (
					<div className={researchBannerStyle}>
						<div className={researchBannerInnerStyle}>
							<h2>{pageSettings.blue_banner_heading}</h2>
							<div
								dangerouslySetInnerHTML={{
									__html: pageSettings.blue_banner_text,
								}}
							/>
						</div>
						<div className={researchBannerButtonSection}>
							<Link className={researchButton} to={pageSettings.blue_banner_button_url}>
								{pageSettings.blue_banner_button_text}
							</Link>
						</div>
					</div>
				)}
			</React.Fragment>
		);
	}
}

export const query = graphql`
	fragment employmentPositionItem on EmploymentJson {
		id
		title
		extract
		recruitment_open_date
		recruitment_close_date
	}
	fragment employmentPosition on EmploymentJson {
		id
		title
		organisation
		organisation_logo
		extract
	}

	fragment employmentPositions on EmploymentJsonConnection {
		edges {
			node {
				...employmentPosition
			}
		}
	}

	query EmploymentPageQuery($type: String!, $skip: Int, $limit: Int) {
		page: allContentJson(filter: { id: { regex: "/content\\/employment-page\\.json/" } }) {
			edges {
				node {
					heading
					body
					blue_banner_heading
					blue_banner_text
					blue_banner_button_text
					blue_banner_button_url
				}
			}
		}

		employment: allEmploymentJson(
			filter: { type: { eq: $type } }
			sort: { order: DESC, fields: [recruitment_open_date] }
			skip: $skip
			limit: $limit
		) {
			...employmentPositions
		}
	}
`;



// WEBPACK FOOTER //
// ./src/templates/employment/index.js